import { formatAuthors } from '@/util/formatAuthors';

/**
 * What a dot phrase cites. Authors stay an array from the source to the byline
 * so truncation never has to re-split a joined string, and `pmid` is set only
 * for a real PubMed record. A trial registration carries its `nct` instead,
 * and its phase and status as `summary`, the line a clinician scans a trial by.
 */
export type Citation = {
  key: string;
  title: string;
  authors: string[];
  journal: string | null;
  year: number | null;
  pmid: string | null;
  url: string;
  nct?: string | null;
  summary?: string | null;
};

const REFERENCES_HEADING = 'References:';

const NUMBERED_ENTRY = /^(\d+)\.\s/;

/**
 * Formats a citation as an AMA-style line: up to three authors then "et al",
 * title, journal, year, the PMID for PubMed records, then the URL. A trial
 * registration names its identifier where the journal goes, as AMA cites one:
 * "ClinicalTrials.gov identifier: NCT04487080".
 *
 * The URL is what makes the citation usable: chat bubbles only linkify URLs,
 * and a DOI-only or web record has no other identifier a reader can follow.
 * It also tells the backend the message already carries a reference, which
 * looks for a URL and nothing else. It is labeled "Link:" so an author who
 * did not type it can tell what the address is for.
 */
export function formatCitation(citation: Citation): string {
  const parts = [
    formatAuthors(citation.authors, ', et al'),
    citation.title.replace(/\.$/, ''),
    citation.nct ? `ClinicalTrials.gov identifier: ${citation.nct}` : (citation.journal ?? ''),
    citation.year ? String(citation.year) : '',
  ].filter(Boolean);

  const pmid = citation.pmid ? ` PMID: ${citation.pmid}` : '';

  const link = citation.url ? ` Link: ${citation.url}` : '';

  return `${parts.join('. ')}.${pmid}${link}`;
}

/**
 * One-line source summary for a result row: first author, venue, year, and
 * the PMID only when there is one. The first author leads because clinicians
 * know a paper by it — "Moreau et al" — and often search by it. A trial shows
 * its phase and status in place of the venue, then its NCT number.
 */
export function describeCitation(citation: Citation): string {
  const [firstAuthor] = citation.authors.map((author) => author.trim()).filter(Boolean);

  const byline = firstAuthor && citation.authors.length > 1 ? `${firstAuthor} et al` : firstAuthor;

  return [
    byline,
    citation.summary ?? citation.journal,
    citation.year,
    citation.pmid ? `PMID ${citation.pmid}` : '',
    citation.nct ?? '',
  ]
    .filter(Boolean)
    .join(' · ');
}

/**
 * Expands a dot phrase into a citation, OpenEvidence style: the token at
 * `tokenStart` becomes an inline `[n]` marker and the formatted citation is
 * appended to a trailing `References:` block, continuing its numbering. Text
 * typed below an existing block moves above it, so the list stays last.
 *
 * The result ends on a fresh line so the caret lands below the list, not at
 * the end of the last entry; prose typed on an entry's own line would be
 * indistinguishable from the entry. Sending trims the trailing newline.
 */
export function insertCitation(draft: string, tokenStart: number, citation: Citation): string {
  const body = draft.slice(0, tokenStart);

  const headingIndex = body.lastIndexOf(`\n${REFERENCES_HEADING}\n`);

  const blockLines =
    headingIndex === -1 ? [] : body.slice(headingIndex + REFERENCES_HEADING.length + 2).split('\n');

  const existingEntries = blockLines.filter((line) => NUMBERED_ENTRY.test(line));

  const textTypedAfterBlock = blockLines
    .filter((line) => !NUMBERED_ENTRY.test(line))
    .join('\n')
    .trim();

  const prose = [headingIndex === -1 ? body : body.slice(0, headingIndex), textTypedAfterBlock]
    .map((section) => section.trim())
    .filter(Boolean)
    .join('\n\n');

  const citationNumber = existingEntries.length + 1;

  const entries = [...existingEntries, `${citationNumber}. ${formatCitation(citation)}`];

  const marker = prose === '' ? `[${citationNumber}]` : ` [${citationNumber}]`;

  return `${prose}${marker}\n\n${REFERENCES_HEADING}\n${entries.join('\n')}\n`;
}
